import { useCallback, useEffect, useState } from "react";
import appLogger from "../../infra/logger/appLogger";
import { useAppDispatch, useAppSelector } from "../hook";
import { useTranslation } from "../localization";
import { RateLimitFlows, useRateLimitErrors } from "../miscellaneous";
import { attachLorebook, cloneLorebook, createLorebook, createLorebookEntry, deleteLorebook, deleteLorebookEntry, detachLorebook, getLorebookById, getLorebooks, importLorebook, reportLorebook, submitLorebookModeration, suggestLorebookField, updateLorebook, updateLorebookEntry, } from "./lorebookSlice";
import { LorebookFlow } from "./types";
const lorebookLogger = appLogger.withTag("Lorebook");
export default function useLorebook() {
    const { t } = useTranslation();
    const { getMessage: getRateLimitErrorMessage } = useRateLimitErrors();
    const dispatch = useAppDispatch();
    const [lorebookData, setLorebookData] = useState(null);
    const [lorebooksData, setLorebooksData] = useState(null);
    const { loading, loadingType } = useAppSelector((state) => state.lorebook);
    const [lorebookError, setLorebookError] = useState(null);
    const resetError = () => {
        setLorebookError(null);
    };
    const getLorebookData = useCallback(async (lorebookId, options) => {
        try {
            setLorebookError(null);
            const result = await dispatch(getLorebookById({ id: lorebookId, ...(options ?? {}) })).unwrap();
            setLorebookData(result);
            return { data: result, error: null };
        }
        catch (error) {
            const apiError = error;
            const errorMessage = apiError?.message;
            setLorebookError({
                errorType: LorebookFlow.GET_LOREBOOK,
                errorMessage: errorMessage,
                errorCode: apiError?.errorCode,
            });
            lorebookLogger.error(`Error fetching lorebook: ${errorMessage}`, apiError);
            return { data: null, error: errorMessage };
        }
    }, [dispatch]);
    const getLorebooksData = useCallback(async (triggerError) => {
        try {
            setLorebookError(null);
            const result = await dispatch(getLorebooks({ triggerError })).unwrap();
            setLorebooksData(result);
            return { data: result, error: null };
        }
        catch (error) {
            const apiError = error;
            const errorMessage = apiError?.message;
            setLorebookError({
                errorType: LorebookFlow.GET_LOREBOOKS,
                errorMessage: errorMessage,
                errorCode: apiError?.errorCode,
            });
            lorebookLogger.error(`Error fetching lorebooks: ${errorMessage}`, apiError);
            return { data: null, error: errorMessage, errorPayload: apiError };
        }
    }, [dispatch]);
    const onUserCreateLorebook = useCallback(async (lorebookData) => {
        try {
            setLorebookError(null);
            const result = await dispatch(createLorebook(lorebookData)).unwrap();
            setLorebookData(result);
            return { data: result, error: null };
        }
        catch (error) {
            const apiError = error;
            const errorMessage = apiError.message || t("error:fallbackError");
            setLorebookError({
                errorType: LorebookFlow.CREATE_LOREBOOK,
                errorMessage: errorMessage,
                errorCode: apiError?.errorCode,
            });
            lorebookLogger.error(`Error creating lorebook: ${errorMessage}`, apiError);
            return { data: null, error: errorMessage, errorPayload: apiError };
        }
    }, [dispatch, getRateLimitErrorMessage, t]);
    const onUserImportLorebook = useCallback(async (payload) => {
        try {
            setLorebookError(null);
            const normalizedPayload = typeof payload === "string" ? JSON.parse(payload) : payload;
            const result = await dispatch(importLorebook(normalizedPayload)).unwrap();
            setLorebookData(result);
            return { data: result, error: null };
        }
        catch (error) {
            const apiError = error;
            const errorMessage = apiError.message || t("error:fallbackError");
            setLorebookError({
                errorType: LorebookFlow.IMPORT_LOREBOOK,
                errorMessage,
                errorCode: apiError?.errorCode,
            });
            lorebookLogger.error(`Error importing lorebook: ${errorMessage}`, apiError);
            return {
                data: null,
                error: errorMessage,
                errorPayload: apiError,
            };
        }
    }, [dispatch, t]);
    const onUserCreateLorebookEntry = useCallback(async (id, entryData) => {
        try {
            setLorebookError(null);
            const result = await dispatch(createLorebookEntry({ id, params: entryData })).unwrap();
            return { data: result, error: null };
        }
        catch (error) {
            const apiError = error;
            const errorMessage = apiError.message || t("error:fallbackError");
            lorebookLogger.error(`Error creating lorebook entry: ${errorMessage}`, apiError);
            return { data: null, error: errorMessage, errorPayload: apiError };
        }
    }, [dispatch, getRateLimitErrorMessage, t]);
    const onUserUpdateLorebook = useCallback(async (id, lorebookData) => {
        try {
            setLorebookError(null);
            const result = await dispatch(updateLorebook({ id, params: lorebookData })).unwrap();
            setLorebookData(result);
            return { data: result, error: null };
        }
        catch (error) {
            const apiError = error;
            const errorMessage = apiError?.message;
            setLorebookError({
                errorType: LorebookFlow.UPDATE_LOREBOOK,
                errorMessage: errorMessage,
                errorCode: apiError?.errorCode,
            });
            lorebookLogger.error(`Error updating lorebook: ${errorMessage}`, error);
            return { data: null, error: errorMessage, errorPayload: apiError };
        }
    }, [dispatch]);
    const onUserSubmitLorebookModeration = useCallback(async (id, params) => {
        try {
            setLorebookError(null);
            const result = await dispatch(submitLorebookModeration({ id, params })).unwrap();
            setLorebookData(result);
            return { data: result, error: null };
        }
        catch (error) {
            const apiError = error;
            const errorMessage = apiError?.message;
            setLorebookError({
                errorType: LorebookFlow.SUBMIT_LOREBOOK_MODERATION,
                errorMessage: errorMessage,
                errorCode: apiError?.errorCode,
            });
            lorebookLogger.error(`Error submitting lorebook for moderation: ${errorMessage}`, error);
            return { data: null, error: errorMessage, errorPayload: apiError };
        }
    }, [dispatch]);
    const onUserUpdateLorebookEntry = useCallback(async (id, entryId, lorebookData) => {
        try {
            setLorebookError(null);
            const result = await dispatch(updateLorebookEntry({ id, entryId, params: lorebookData })).unwrap();
            return { data: result, error: null };
        }
        catch (error) {
            const apiError = error;
            const errorMessage = apiError?.message || t("error:fallbackError");
            lorebookLogger.error(`Error updating lorebook: ${errorMessage}`, error);
            return { data: null, error: errorMessage, errorPayload: apiError };
        }
    }, [dispatch, t]);
    const onUserDeleteLorebook = useCallback(async (id) => {
        try {
            setLorebookError(null);
            const result = await dispatch(deleteLorebook({ id })).unwrap();
            return { data: result, error: null };
        }
        catch (error) {
            const apiError = error;
            const errorMessage = apiError?.message;
            setLorebookError({
                errorType: LorebookFlow.DELETE_LOREBOOK,
                errorMessage: errorMessage,
                errorCode: apiError?.errorCode,
            });
            lorebookLogger.error(`Error deleting lorebook: ${errorMessage}`, apiError);
            return { data: null, error: errorMessage, errorPayload: apiError };
        }
    }, [dispatch]);
    const onUserDeleteLorebookEntry = useCallback(async (id, entryId) => {
        try {
            setLorebookError(null);
            const result = await dispatch(deleteLorebookEntry({ id, entryId })).unwrap();
            return { data: result, error: null };
        }
        catch (error) {
            const apiError = error;
            const errorMessage = apiError?.message;
            lorebookLogger.error(`
          Error deleting lorebook entry: ${errorMessage}
          `, apiError);
            return { data: null, error: errorMessage, errorPayload: apiError };
        }
    }, [dispatch]);
    const onUserAttachLorebook = useCallback(async (id, params) => {
        try {
            setLorebookError(null);
            await dispatch(attachLorebook({ id, params })).unwrap();
            return { data: null, error: null };
        }
        catch (error) {
            const apiError = error;
            const errorMessage = apiError?.message || t("error:fallbackError");
            setLorebookError({
                errorType: LorebookFlow.ATTACH_LOREBOOK,
                errorMessage: errorMessage,
                errorCode: apiError?.errorCode,
            });
            lorebookLogger.error(`Error attaching lorebook: ${errorMessage}`, apiError);
            return { data: null, error: errorMessage, errorPayload: apiError };
        }
    }, [dispatch, t]);
    const onUserDetachLorebook = useCallback(async (id, characterId) => {
        try {
            setLorebookError(null);
            const result = await dispatch(detachLorebook({ id, characterId })).unwrap();
            return { data: result, error: null };
        }
        catch (error) {
            const apiError = error;
            const errorMessage = apiError?.message || t("error:fallbackError");
            setLorebookError({
                errorType: LorebookFlow.DETACH_LOREBOOK,
                errorMessage: errorMessage,
                errorCode: apiError?.errorCode,
            });
            lorebookLogger.error(`Error detaching lorebook: ${errorMessage}`, apiError);
            return { data: null, error: errorMessage, errorPayload: apiError };
        }
    }, [dispatch, t]);
    const onUserSuggestLorebookField = useCallback(async (params) => {
        try {
            setLorebookError(null);
            const result = await dispatch(suggestLorebookField(params)).unwrap();
            return { data: result, error: null };
        }
        catch (error) {
            const apiError = error;
            const errorMessage = getRateLimitErrorMessage({
                errorCode: apiError?.errorCode,
                skipDefaultMessage: true,
                errorContext: {
                    flow: RateLimitFlows.SUGGEST_FIELD,
                },
            }) ||
                apiError.message ||
                t("error:fallbackError");
            setLorebookError({
                errorType: LorebookFlow.SUGGEST_FIELD,
                errorMessage: errorMessage,
                errorCode: apiError?.errorCode,
            });
            lorebookLogger.error(`Error suggesting lorebook field: ${errorMessage}`, apiError);
            return { data: null, error: errorMessage, errorPayload: apiError };
        }
    }, [dispatch, getRateLimitErrorMessage, t]);
    useEffect(() => {
        return () => {
            // This cleanup function will run when the component unmounts
            setLorebookData(null);
            setLorebooksData(null);
        };
    }, []);
    const onUserCloneLorebook = useCallback(async (id) => {
        try {
            setLorebookError(null);
            const result = await dispatch(cloneLorebook({ id })).unwrap();
            setLorebookData(result);
            return { data: result, error: null };
        }
        catch (error) {
            const apiError = error;
            const errorMessage = apiError?.message || t("error:fallbackError");
            setLorebookError({
                errorType: LorebookFlow.CLONE_LOREBOOK,
                errorMessage: errorMessage,
                errorCode: apiError?.errorCode,
            });
            lorebookLogger.error(`Error cloning lorebook: ${errorMessage}`, apiError);
            return { data: null, error: errorMessage, errorPayload: apiError };
        }
    }, [dispatch, t]);
    const onUserReportLorebook = useCallback(async (lorebookId, reportData) => {
        try {
            setLorebookError(null);
            const result = await dispatch(reportLorebook({ id: lorebookId, params: reportData })).unwrap();
            return { data: result, error: null };
        }
        catch (error) {
            const apiError = error;
            const errorMessage = getRateLimitErrorMessage({
                errorCode: apiError?.errorCode,
                skipDefaultMessage: true,
                errorContext: {
                    flow: RateLimitFlows.REPORT_LOREBOOK,
                },
            }) ||
                apiError.message ||
                t("error:fallbackError");
            setLorebookError({
                errorType: LorebookFlow.REPORT_LOREBOOK,
                errorMessage: errorMessage,
                errorCode: apiError?.errorCode,
            });
            lorebookLogger.error(`Error reporting lorebook: ${errorMessage}`, apiError);
            return { data: null, error: errorMessage, errorPayload: apiError };
        }
    }, [dispatch, getRateLimitErrorMessage, t]);
    return {
        lorebookData,
        lorebooksData,
        isLoading: loading,
        loadingType,
        errorType: lorebookError?.errorType || null,
        errorMessage: lorebookError?.errorMessage || null,
        errorCode: lorebookError?.errorCode || null,
        getLorebookData,
        getLorebooksData,
        onUserCreateLorebook,
        onUserImportLorebook,
        onUserUpdateLorebook,
        onUserSubmitLorebookModeration,
        onUserDeleteLorebook,
        onUserCreateLorebookEntry,
        onUserUpdateLorebookEntry,
        onUserDeleteLorebookEntry,
        onUserAttachLorebook,
        onUserDetachLorebook,
        onUserSuggestLorebookField,
        onUserReportLorebook,
        onUserCloneLorebook,
        resetError,
    };
}
